import { useState, useCallback } from 'react';
import { useAnimation, useSelectedObject } from '@/context/AnimationContext';
import { useI18n } from '@/context/I18nContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';

const BATELKA_PARTS = [
  { id: 'fill', key: 'charBody', default: '#f8a4c8' },
  { id: 'hairColor', key: 'charHair', default: '#3b1f0b' },
  { id: 'skinColor', key: 'charSkin', default: '#fde8d0' },
  { id: 'dressColor', key: 'charDress', default: '#f8a4c8' },
  { id: 'shoeColor', key: 'charShoes', default: '#d4608a' },
  { id: 'eyeColor', key: 'charEyes', default: '#1e1e1e' },
];

const BLONDE_BALLERINA_PARTS = [
  { id: 'fill', key: 'charBody', default: '#f9a8d4' },
  { id: 'hairColor', key: 'charHair', default: '#f5d060' },
  { id: 'skinColor', key: 'charSkin', default: '#fde8d0' },
  { id: 'dressColor', key: 'charDress', default: '#f9a8d4' },
  { id: 'shoeColor', key: 'charShoes', default: '#ec4899' },
  { id: 'eyeColor', key: 'charEyes', default: '#3b82f6' },
];

const SUN_PARTS = [
  { id: 'fill', key: 'charBody', default: '#fbbf24' },
  { id: 'rayColor', key: 'charDetail1', default: '#fde047' },
];

const SINGER_PARTS = [
  { id: 'fill', key: 'charBody', default: '#8b5cf6' },
  { id: 'hairColor', key: 'charHair', default: '#f5d060' },
  { id: 'skinColor', key: 'charSkin', default: '#fde8d0' },
  { id: 'dressColor', key: 'charDress', default: '#8b5cf6' },
  { id: 'shoeColor', key: 'charShoes', default: '#4f46e5' },
  { id: 'eyeColor', key: 'charEyes', default: '#10b981' },
];

const AGATA_PARTS = [
  { id: 'fill', key: 'charBody', default: '#4ade80' },
  { id: 'hairColor', key: 'charHair', default: '#fde047' },
  { id: 'skinColor', key: 'charSkin', default: '#fde8d0' },
  { id: 'greenTop', key: 'charClothing', default: '#22c55e' },
  { id: 'hairTieColor', key: 'charAccessory', default: '#ef4444' },
  { id: 'segmentYellow', key: 'charDetail1', default: '#fbbf24' },
  { id: 'segmentOrange', key: 'charDetail2', default: '#fb923c' },
];

const COLOR_PRESETS = [
  '#f43f5e', '#ec4899', '#f8a4c8', '#d946ef', '#a855f7', '#8b5cf6',
  '#6366f1', '#3b82f6', '#0ea5e9', '#14b8a6', '#10b981', '#22c55e',
  '#4ade80', '#84cc16', '#eab308', '#f59e0b', '#fbbf24', '#fb923c',
  '#f97316', '#ef4444', '#1e1e1e', '#374151', '#6b7280', '#d1d5db',
  '#fde8d0', '#fef3c7', '#ffffff', '#fde047', '#3b1f0b', '#78350f',
];

export function CharacterEditor({ trigger }) {
  const [open, setOpen] = useState(false);
  const { state, dispatch } = useAnimation();
  const { t } = useI18n();
  const selectedObj = useSelectedObject();

  const isCharacter = selectedObj && ['character', 'agata', 'blondeBallerina', 'sun', 'singer'].includes(selectedObj.type);
  const partsMap = {
    character: BATELKA_PARTS,
    blondeBallerina: BLONDE_BALLERINA_PARTS,
    sun: SUN_PARTS,
    singer: SINGER_PARTS,
    agata: AGATA_PARTS,
  };
  const parts = partsMap[selectedObj?.type] || BATELKA_PARTS;
  const charColors = selectedObj?.customColors || {};

  const updateColor = useCallback((partId, color) => {
    if (!selectedObj) return;
    const newColors = { ...charColors, [partId]: color };
    dispatch({
      type: 'UPDATE_OBJECT',
      payload: {
        id: selectedObj.id,
        props: {
          customColors: newColors,
          ...(partId === 'fill' ? { fill: color } : {}),
        },
      }
    });
  }, [selectedObj, charColors, dispatch]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm bg-[hsl(220,14%,11%)] border-white/[0.08]" data-testid="character-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-sm">{t('characterEditor')}</DialogTitle>
          <DialogDescription className="text-[10px] text-muted-foreground/60">{t('characterEditorHint')}</DialogDescription>
        </DialogHeader>

        {!isCharacter ? (
          <div className="py-8 text-center">
            <p className="text-xs text-muted-foreground/50">{t('selectCharacterFirst')}</p>
          </div>
        ) : (
          <ScrollArea className="max-h-[400px]">
            <div className="space-y-4 pr-2">
              <div className="text-[10px] font-mono text-muted-foreground/60 px-1 py-1 bg-white/[0.02] rounded">
                {selectedObj.type === 'agata' ? 'Agata' : 'Batelka'} - {selectedObj.name}
              </div>

              {parts.map(part => (
                <div key={part.id} data-testid={`char-editor-part-${part.id}`}>
                  <Label className="text-[10px] font-mono text-muted-foreground/70 mb-1.5 block">
                    {t(part.key)}
                  </Label>
                  <div className="flex items-center gap-2">
                    <div className="flex flex-wrap gap-1">
                      {COLOR_PRESETS.map(color => (
                        <button
                          key={color}
                          className={`w-5 h-5 rounded-md border transition-all hover:scale-110 ${
                            (charColors[part.id] || part.default) === color
                              ? 'border-white/40 ring-1 ring-white/20 scale-110'
                              : 'border-white/[0.06]'
                          }`}
                          style={{ backgroundColor: color }}
                          onClick={() => updateColor(part.id, color)}
                          data-testid={`color-${part.id}-${color.replace('#', '')}`}
                        />
                      ))}
                    </div>
                  </div>
                  <input
                    type="color"
                    className="mt-1.5 h-6 w-full rounded cursor-pointer bg-transparent border border-white/[0.06]"
                    value={charColors[part.id] || part.default}
                    onChange={(e) => updateColor(part.id, e.target.value)}
                    data-testid={`color-picker-${part.id}`}
                  />
                </div>
              ))}
            </div>
          </ScrollArea>
        )}
      </DialogContent>
    </Dialog>
  );
}
